import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useFileManagerStore } from '../../store/useFileManagerStore';
import { buildContextMenu, MenuItem } from '../../utils/menuBuilder';
import { ChevronRight, Check } from 'lucide-react';
import { eventBus } from '../../../../core/EventBus';
import { t } from '../../i18n';

interface ContextMenuProps {
  x: number;
  y: number;
  targetPath?: string;
  onClose: () => void;
}

const SubMenu: React.FC<{ items: MenuItem[], onClose: () => void }> = ({ items, onClose }) => {
    return (
        <div className="absolute left-full top-[-4px] w-48 bg-[#202020]/95 backdrop-blur-xl border border-white/10 rounded-lg shadow-2xl py-1 z-[210]">
            <div className="absolute -left-2 top-0 bottom-0 w-2 bg-transparent" />
            {items.map((item, idx) => <MenuItemComponent key={item.id || idx} item={item} onClose={onClose} />)}
        </div>
    );
};

const MenuItemComponent: React.FC<{ item: MenuItem, onClose: () => void }> = ({ item, onClose }) => {
    const [showSub, setShowSub] = useState(false);
    const timeoutRef = useRef<number | null>(null);

    if (item.separator) {
        return <div className="h-px bg-white/10 my-1 mx-2" />;
    }

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (item.disabled) return;

        if (item.action && !item.children) {
            item.action();
            onClose();
        }
    };

    const handleMouseEnter = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        setShowSub(true);
    };

    const handleMouseLeave = () => {
        timeoutRef.current = window.setTimeout(() => {
            setShowSub(false);
        }, 150); 
    };

    return (
        <div 
            className={`flex items-center px-3 py-1.5 rounded cursor-pointer group relative mx-1
                ${item.disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-white/10'} 
                ${item.color || 'text-slate-200'}
            `}
            onClick={handleClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <div className="w-5 flex items-center justify-center mr-2">
                {item.checked && <Check size={14} className="text-win-accent" />}
                {item.icon && !item.checked && <item.icon size={16} className="opacity-70 group-hover:opacity-100" />}
            </div>
            
            <span className="flex-1 text-sm truncate">{item.label}</span>
            
            {item.shortcut && <span className="text-xs text-slate-500 ml-4">{item.shortcut}</span>}
            {item.children && <ChevronRight size={14} className="ml-2 text-slate-500" />}

            {/* Submenu */}
            {item.children && showSub && <SubMenu items={item.children} onClose={onClose} />}
        </div>
    );
};

const ContextMenu: React.FC<ContextMenuProps> = ({ x, y, targetPath, onClose }) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const store = useFileManagerStore();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Viewport checking
  const menuWidth = 224; 
  const menuHeight = 300; 
  
  const adjustedX = x + menuWidth > window.innerWidth ? x - menuWidth : x;
  const adjustedY = y + menuHeight > window.innerHeight ? y - menuHeight : y;

  const style = { top: adjustedY, left: adjustedX };

  // 在远程桌面定位/打开：逐个 shellOpen，多选先二次确认，完成后关闭文件管理器。
  const remoteAction = async (paths: string[], mode: 'open' | 'select') => {
    if (paths.length > 1) {
      const msg = mode === 'select'
        ? t('contextRemoteLocateMultiConfirm', { count: paths.length })
        : t('contextRemoteOpenMultiConfirm', { count: paths.length });
      if (!window.confirm(msg)) return;
    }
    for (const p of paths) {
      try { await (store.fs as any).shellOpen?.(p, mode); } catch { /* 单项失败忽略，继续其余 */ }
    }
    eventBus.emit('fm:close', undefined as never);
  };

  const actions = {
      download: store.downloadFiles,
      share: (paths: string[]) => store.openShareDialog(paths),
      copyLink: store.copyLink,
      preview: (path: string) => {
          const file = store.files.find(f => f.path === path);
          if (file) store.openPreview(file);
      },
      edit: (path: string) => {
          const file = store.files.find(f => f.path === path);
          if (file) store.openEditor(file);
      },
      unzip: store.unzip,
      rename: store.startRenaming,
      copy: store.copyFiles,
      delete: store.deleteFiles,
      copyPath: store.copyPath,
      newFolder: () => store.createNewFolder(store.currentPath),
      paste: store.pasteFiles,
      sort: (key: string, dir: 'asc' | 'desc') => store.setSortConfig({ key: key as any, direction: dir }),
      pin: store.pinFolder,
      unpin: store.unpinFolder,
      uploadFile: (targetPath: string) => store.triggerUpload('file', targetPath),
      uploadFolder: (targetPath: string) => store.triggerUpload('folder', targetPath),
      locateInRemote: (paths: string[]) => remoteAction(paths, 'select'),
      openInRemote: (paths: string[]) => remoteAction(paths, 'open'),
  };

  const items = buildContextMenu({
      selectedPaths: store.selectedPaths,
      files: store.files,
      currentPath: store.currentPath,
      clipboard: store.clipboard,
      sortConfig: store.sortConfig,
      quickAccessItems: store.quickAccessItems,
      actions
  });

  return createPortal(
    <div
      ref={menuRef}
      className="fixed z-[200] w-56 bg-[#202020]/95 backdrop-blur-xl border border-white/10 rounded-lg shadow-2xl py-1 text-slate-200 animate-in fade-in zoom-in-95 duration-100"
      style={style}
      onContextMenu={(e) => e.preventDefault()}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()} // Crucial: Stop PointerDown bubbling to MainView
    >
      {items.map((item, idx) => (
          <MenuItemComponent key={item.id || idx} item={item} onClose={onClose} />
      ))}
    </div>,
    document.body
  );
};

export default ContextMenu;
